/**
 * Memory-answer card. Source chips show how many notes backed the
 * answer — each chip's `title` is the full note UUID for debugging.
 */
import { AssistantBubble } from "./AssistantBubble";
import { Markdown } from "../../components/Markdown";
import { RelativeTime } from "../../components/RelativeTime";
import type { AskAnswerMessage } from "../types";

interface Props {
  message: AskAnswerMessage;
}

const MODE_TITLE: Record<AskAnswerMessage["mode"], string> = {
  answer: "Memory answer",
  transcript: "From transcript",
  list: "From memory",
};

export function AskAnswerBubble({ message }: Props): React.ReactElement {
  return (
    <AssistantBubble
      title={MODE_TITLE[message.mode]}
      meta={<RelativeTime iso={message.createdAt} />}
    >
      {message.content ? (
        // `mode="transcript"` is the user's own verbatim text — render
        // as plain whitespace-preserving text, NOT through Markdown
        // (their asterisks shouldn't be interpreted as emphasis).
        message.mode === "transcript" ? (
          <div className="whitespace-pre-wrap">{message.content}</div>
        ) : (
          <Markdown text={message.content} />
        )
      ) : (
        <span style={{ color: "var(--color-hint)" }}>(empty)</span>
      )}
      {message.citedNoteIds.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {message.citedNoteIds.map((id, idx) => (
            <span
              key={id}
              className="text-[10px] px-1.5 py-0.5 rounded-full"
              style={{
                background: "var(--color-bg)",
                color: "var(--color-hint)",
                boxShadow: "var(--shadow-1)",
              }}
              title={id}
            >
              source {idx + 1}
            </span>
          ))}
        </div>
      ) : null}
    </AssistantBubble>
  );
}
